// 非編集表示・コメント本文が共有する、Markdown のコードブロック（``` フェンス）の描画。
//
// - 中身の行は Markdown として一切解釈しない（見出し・箇条書き・引用・表・リンク・インライン装飾の
//   いずれにもならない）。表示テキストは元テキストから行の raw 範囲を切り出した生の文字列で、
//   分割は部分書式（run）の境界だけで起きる（markdownCodeBlockLines）。
//   解釈を通さないため、表のセルのようにリンク描画を呼び出し側へ委ねる必要がなく、
//   キャンバスオブジェクトとコメント本文が同じコンポーネントをそのまま使える。
//   表示文字の描き方だけは `renderText` で差し替えられる（キャンバスオブジェクトが検索語の強調を
//   重ねるための口）。渡さない呼び出しの構造は従来とまったく同じになる。
// - フェンス行は表示しない。中身が 0 行のブロックは、開始フェンス行の index を持つ
//   `<br>` だけの span を 1 つ置いて 1 行分の高さを確保する。
// - `data-md-line`（raw 行 index）は行 span だけに付ける。`pre` / `code` へ付けると、
//   行要素を引くセレクタが `pre` 全体を選び、範囲選択の矩形収集で `pre` と各行 span が
//   二重に集まる。
// - 計測ビルダー（buildMarkdownTextContent の appendMeasuredCodeBlock）と同じ構造を作る
//   （構造一致はパリティテストで担保）。

import { Fragment, type CSSProperties, type ReactNode } from "react";
import type {
  MarkdownCodeBlock,
  MarkdownSourceMap,
  TextInlineStyleRange,
} from "@refinement-canvas/domain";
import {
  markdownCodeBlockLines,
  markdownCodeSpanCss,
  MARKDOWN_CODE_CLASS,
} from "../../../lib/markdown-text";

interface Props {
  /** 本文全体の対応表（行の raw 範囲の切り出しに使う）。 */
  readonly sourceMap: MarkdownSourceMap;
  readonly code: MarkdownCodeBlock;
  /** 文字範囲だけへ適用する部分書式（正規形の run 配列）。 */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /**
   * 表示文字の描き方。未指定なら素の文字列をそのまま描く（従来の構造）。
   * キャンバスオブジェクトの本文だけが、検索語ハイライトを重ねるために渡す
   * （同じ部品を使うコメント本文は強調の対象外のため渡さない）。
   */
  readonly renderText?: ((text: string) => ReactNode) | undefined;
}

export function MarkdownCodeBlockView({
  sourceMap,
  code,
  inlineStyles,
  renderText,
}: Props): JSX.Element {
  const renderPieceText = (text: string): ReactNode => (renderText ? renderText(text) : text);
  return (
    <pre className={MARKDOWN_CODE_CLASS}>
      <code>
        {markdownCodeBlockLines(sourceMap, code, inlineStyles).map((line) => (
          <span key={line.lineIndex} data-md-line={line.lineIndex}>
            {/* 空の中身行・空ブロックは <br> で 1 行分の高さを維持する（空 span は高さ 0 になるため）。 */}
            {line.text.length === 0 ? (
              <br />
            ) : (
              line.pieces.map((piece, index) => {
                const css = markdownCodeSpanCss(piece.style);
                // 上書きの無い区間は span で包まず素のテキストにする（計測ビルダーと同じ構造）。
                // key は分割結果のインデックス。pieces は本文と run から毎回同じ順序で導出される。
                if (css === undefined)
                  return <Fragment key={index}>{renderPieceText(piece.text)}</Fragment>;
                return (
                  <span key={index} style={css as CSSProperties}>
                    {renderPieceText(piece.text)}
                  </span>
                );
              })
            )}
          </span>
        ))}
      </code>
    </pre>
  );
}
